// File: noviopus-ats-client/src/features/Job/jobCreateSlice.ts
import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { JobCreateState, JobDraft, DEFAULT_STAGES } from './types';

const initialDraft: JobDraft = {
  id: '',
  _id: '',
  companyId: '', 
  title: '',
  type: 1, 
  numRecruits: 1,
  description: '',
  salaryPeriod: 'monthly',
  salaryFrom: '',
  salaryTo: '',
  currency: 'USD',
  useOrganizationDefaults: true,
  locationId: '',
  isWorkFromHome: false,
  distance: 2,
  
  skills: [],
  languages: [],
  educations: [],
  workExperience: [],
  
  workflowId: '',
  workflowName: '',
  hiringManager: null,
  teamMembers: [],
  stages: DEFAULT_STAGES
};

export const initialState: JobCreateState = {
  draft: initialDraft,
  loading: false,
  error: null,
  saveStatus: 'idle',
  showOrgSelector: false,
  parsingStatus: 'idle',
  parsedData: null 
};

export const fetchJobDetails = createAction<string>('jobCreate/fetchJobDetails');
export const startPreFlightJDParsing = createAction<{ description: string }>('jobCreate/startPreFlightJDParsing');
export const invalidatePreFlight = createAction('jobCreate/invalidatePreFlight');

const jobCreateSlice = createSlice({
  name: 'jobCreate',
  initialState,
  reducers: {
    initiateCreateJob: (state) => {
        state.loading = true;
        state.error = null;
        state.saveStatus = 'idle';
    },

    setJobCreationContext: (state, action: PayloadAction<string>) => {
        console.log(`[JOB_TRACE] setJobCreationContext: Setting companyId to ${action.payload}`);
        state.draft.companyId = action.payload;
        state.showOrgSelector = false;
        state.loading = false;
    },

    openOrgSelector: (state) => {
        state.showOrgSelector = true;
        state.loading = false;
    },

    closeOrgSelector: (state) => {
        state.showOrgSelector = false;
        state.loading = false;
    },

    resetJobDraft: (state) => {
        console.log('[JOB_TRACE] resetJobDraft: Resetting draft to initial state');
        state.draft = { ...initialDraft };
        state.saveStatus = 'idle';
        state.error = null;
        state.showOrgSelector = false;
        state.loading = false;
        state.parsingStatus = 'idle';
        state.parsedData = null;
    },

    resetJobStatus: (state) => {
        state.saveStatus = 'idle';
        state.error = null;
    },

    updateDraft: (state, action: PayloadAction<Partial<JobDraft>>) => {
        if (action.payload.companyId !== undefined) {
             console.log(`[JOB_TRACE] updateDraft: Overwriting companyId with ${action.payload.companyId}`);
        }
        state.draft = { ...state.draft, ...action.payload };
    },

    loadJobForEdit: (state, action: PayloadAction<JobDraft>) => {
        console.log(`[JOB_TRACE] loadJobForEdit: Loading job with companyId ${action.payload.companyId}`);
        state.draft = action.payload;
        state.loading = false;
    },

    submitJob: (state, action: PayloadAction<{ nextPath?: string; nextStep?: string; isPublishingFree?: boolean } | undefined>) => {
        console.log(`[JOB_TRACE] submitJob: Submitting with companyId: ${state.draft.companyId}`);
        state.loading = true;
        state.saveStatus = 'saving';
        state.error = null;
    },

    submitJobSuccess: (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.saveStatus = 'success';
        if (action.payload) {
            const res = action.payload;
            const id = res.id || res._id;
            if (id) {
                state.draft.id = id;
                state.draft._id = id;
            }

            // Hydrate AI-extracted Requirements into Redux Draft
            if (res.skills && Array.isArray(res.skills) && res.skills.length > 0) {
                state.draft.skills = res.skills.map((s: any) => ({
                    id: s.id || s._id,
                    skillId: s.skillName ? { value: s.skillId, text: s.skillName } : s.skillId,
                    name: s.skillName,
                    level: s.level,
                    type: s.type,
                    required: s.required !== undefined ? s.required : true
                }));
            }

            if (res.workExperience && Array.isArray(res.workExperience) && res.workExperience.length > 0) {
                state.draft.workExperience = res.workExperience.map((w: any) => ({
                    id: w.id || w._id,
                    jobTitleId: w.jobTitleName ? { value: w.jobTitleId, text: w.jobTitleName } : w.jobTitleId,
                    name: w.jobTitleName,
                    level: w.level,
                    required: w.required !== undefined ? w.required : true
                }));
            }

            if (res.educations && Array.isArray(res.educations) && res.educations.length > 0) {
                state.draft.educations = res.educations.map((e: any) => ({
                    id: e.id || e._id,
                    degreeTypeId: e.degreeTypeName ? { value: e.degreeTypeId, text: e.degreeTypeName } : e.degreeTypeId,
                    fieldOfStudyId: e.fieldOfStudyName ? { value: e.fieldOfStudyId, text: e.fieldOfStudyName } : e.fieldOfStudyId,
                    educationLevel: e.educationLevel || 'academic',
                    required: e.required !== undefined ? e.required : true
                }));
            }

            if (res.languages && Array.isArray(res.languages) && res.languages.length > 0) {
                state.draft.languages = res.languages.map((l: any) => ({
                    id: l.id || l._id,
                    languageId: l.languageName ? { value: l.languageId, text: l.languageName } : (l.name ? { value: l.languageId, text: l.name } : l.languageId),
                    name: l.languageName || l.name,
                    level: l.level,
                    required: l.required !== undefined ? l.required : true
                }));
            }

            if (res.isWorkFromHome !== undefined) {
                state.draft.isWorkFromHome = res.isWorkFromHome;
            }

            if (res.location) {
                state.draft.location = res.location;
                const locId = res.location._id || res.location.id || res.location.locationId;
                if (locId) {
                    state.draft.locationId = locId;
                }
            }
        }
    },
    submitJobFailure: (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.saveStatus = 'error';
        state.error = action.payload;
    },

    deleteJobDraft: (state, action: PayloadAction<string>) => {
        state.loading = true;
    },
    deleteJobDraftSuccess: (state) => {
        state.loading = false;
        state.draft = { ...initialDraft };
    },

    startParsing: (state) => {
        state.parsingStatus = 'loading';
    },
    parsingSuccess: (state, action: PayloadAction<any>) => {
        state.parsingStatus = 'success';
        state.parsedData = action.payload;

        // Immediately sync pre-flight requirements to draft state
        if (action.payload) {
            const res = action.payload;
            if (res.skills?.length > 0) {
                state.draft.skills = res.skills.map((s: any) => ({
                    id: s.id || s._id,
                    skillId: s.skillName ? { value: s.skillId, text: s.skillName } : s.skillId,
                    name: s.skillName,
                    level: s.level,
                    type: s.type,
                    required: s.required !== undefined ? s.required : true
                }));
            }
            if (res.workExperience?.length > 0) {
                state.draft.workExperience = res.workExperience.map((w: any) => ({
                    id: w.id || w._id,
                    jobTitleId: w.jobTitleName ? { value: w.jobTitleId, text: w.jobTitleName } : w.jobTitleId,
                    name: w.jobTitleName,
                    level: w.level,
                    required: w.required !== undefined ? w.required : true
                }));
            }
            if (res.educations?.length > 0) {
                state.draft.educations = res.educations.map((e: any) => ({
                    id: e.id || e._id,
                    degreeTypeId: e.degreeTypeName ? { value: e.degreeTypeId, text: e.degreeTypeName } : e.degreeTypeId,
                    fieldOfStudyId: e.fieldOfStudyName ? { value: e.fieldOfStudyId, text: e.fieldOfStudyName } : e.fieldOfStudyId,
                    educationLevel: e.educationLevel || 'academic',
                    required: e.required !== undefined ? e.required : true
                }));
            }
            if (res.languages?.length > 0) {
                state.draft.languages = res.languages.map((l: any) => ({
                    id: l.id || l._id,
                    languageId: l.languageName ? { value: l.languageId, text: l.languageName } : (l.name ? { value: l.languageId, text: l.name } : l.languageId),
                    name: l.languageName || l.name,
                    level: l.level,
                    required: l.required !== undefined ? l.required : true
                }));
            }
            if (res.isWorkFromHome !== undefined) {
                state.draft.isWorkFromHome = res.isWorkFromHome;
            }
        }
    },
    parsingFailure: (state) => {
        state.parsingStatus = 'error';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(startPreFlightJDParsing, (state, action) => {
        state.draft.description = action.payload.description;
      })
      .addCase(invalidatePreFlight, (state) => {
        state.parsingStatus = 'idle';
        state.parsedData = null;
      });
  }
});

export const {
  initiateCreateJob,
  setJobCreationContext,
  openOrgSelector,
  closeOrgSelector,
  resetJobDraft,
  resetJobStatus,
  updateDraft,
  loadJobForEdit,
  submitJob,
  submitJobSuccess,
  submitJobFailure,
  deleteJobDraft,
  deleteJobDraftSuccess,
  startParsing,
  parsingSuccess,
  parsingFailure
} = jobCreateSlice.actions;

export default jobCreateSlice.reducer;